import React from 'react';
import { Bell, Plus } from 'lucide-react';
import { AlertsList } from './AlertsList';
import { NewAlertForm } from './NewAlertForm';
import { getRegistrations, Registration } from '../services/alerts';
import { supabase, verifyConnection } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

export function AlertsScreen() {
  const [showNewAlert, setShowNewAlert] = React.useState(false);
  const [registrations, setRegistrations] = React.useState<Registration[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const loadRegistrations = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Verify connection and session before proceeding
      await verifyConnection();

      // Check if we have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Sessão expirada. Por favor, faça login novamente.');
      }

      const data = await getRegistrations();
      if (!data) {
        throw new Error('Não foi possível carregar os registros');
      }

      setRegistrations(data);
    } catch (err) {
      console.error('Error loading registrations:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar registros';
      setError(errorMessage);

      // If session error, redirect to login
      if (errorMessage.includes('Sessão expirada')) {
        await supabase.auth.signOut();
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  React.useEffect(() => {
    loadRegistrations();
  }, [loadRegistrations]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="text-blue-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-900">
            Meus Alertas
          </h2>
        </div>

        <button
          onClick={() => setShowNewAlert(true)}
          className="w-full mb-6 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
        >
          <Plus size={20} />
          <span>Novo Alerta</span>
        </button>

        {error ? (
          <div className="p-4 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        ) : (
          <AlertsList 
            registrations={registrations}
            isLoading={isLoading}
            onDeleteSuccess={loadRegistrations}
          />
        )}
      </div>

      {showNewAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <NewAlertForm onClose={() => setShowNewAlert(false)} />
          </div>
        </div>
      )}
    </div>
  );
}